import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const PremiumConversionTimeline = ({ data }) => {
  const [selectedStage, setSelectedStage] = useState(null);

  if (!data || data?.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">
        No conversion timeline data available
      </div>
    );
  }

  const getStageColor = (stage) => {
    const colors = {
      'registration': 'primary',
      'first-checkin': 'info',
      'feature-exploration': 'warning',
      'premium-view': 'success',
      'conversion': 'success'
    };
    return colors?.[stage] || 'primary';
  };

  const getStageIcon = (stage) => {
    const icons = {
      'registration': 'UserPlus',
      'first-checkin': 'CheckCircle',
      'feature-exploration': 'Compass',
      'premium-view': 'Eye',
      'conversion': 'CreditCard'
    };
    return icons?.[stage] || 'Circle';
  };

  return (
    <div className="space-y-6">
      {/* Timeline Visualization */}
      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border"></div>

        {/* Timeline Stages */}
        <div className="space-y-6">
          {data?.map((stage, index) => {
            const color = getStageColor(stage?.stageId);
            const icon = getStageIcon(stage?.stageId);
            const isSelected = selectedStage?.stageId === stage?.stageId;

            return (
              <div
                key={index}
                onClick={() => setSelectedStage(stage)}
                className="relative pl-16 cursor-pointer group"
              >
                {/* Timeline Dot */}
                <div
                  className={`absolute left-0 w-12 h-12 rounded-full border-4 border-card bg-${color} flex items-center justify-center transition-transform group-hover:scale-110 ${
                    isSelected ? 'scale-110 ring-4 ring-primary/20' : ''
                  }`}
                >
                  <Icon name={icon} size={20} className="text-white" />
                </div>

                {/* Stage Content */}
                <div
                  className={`p-4 rounded-lg border-2 transition-all ${
                    isSelected
                      ? 'border-primary bg-primary/5' :'border-border bg-card group-hover:border-primary/50 group-hover:bg-muted/30'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-foreground">{stage?.stageName}</h3>
                      <p className="text-xs text-muted-foreground mt-1">{stage?.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Avg Time</p>
                      <p className="text-sm font-bold text-foreground">{stage?.avgTimeToReach}</p>
                    </div>
                  </div>

                  {/* Metrics */}
                  <div className="grid grid-cols-3 gap-3 mt-3">
                    <div className="p-2 bg-background rounded">
                      <p className="text-xs text-muted-foreground mb-1">Users Reached</p>
                      <p className="text-sm font-bold text-foreground">{stage?.usersReached?.toLocaleString()}</p>
                    </div>
                    <div className="p-2 bg-background rounded">
                      <p className="text-xs text-muted-foreground mb-1">Conversion Rate</p>
                      <p className="text-sm font-bold text-success">{stage?.conversionRate}%</p>
                    </div>
                    <div className="p-2 bg-background rounded">
                      <p className="text-xs text-muted-foreground mb-1">Drop-off</p>
                      <p className="text-sm font-bold text-error">{stage?.dropOffRate}%</p>
                    </div>
                  </div>

                  {/* Optimization Tip */}
                  {stage?.optimizationTip && (
                    <div className="mt-3 p-3 bg-warning/10 rounded-lg border border-warning/20">
                      <div className="flex items-start gap-2">
                        <Icon name="Lightbulb" size={14} className="text-warning mt-0.5" />
                        <p className="text-xs text-foreground">{stage?.optimizationTip}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Conversion Velocity Analysis */}
      <div className="p-4 bg-gradient-to-br from-success/10 to-primary/10 rounded-lg border border-success/20">
        <div className="flex items-center gap-2 mb-3">
          <Icon name="Zap" size={18} className="text-success" />
          <h3 className="font-semibold text-foreground">Conversion Velocity Insights</h3>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Fastest Conversion</p>
            <p className="text-lg font-bold text-success">2.3 days</p>
            <p className="text-xs text-muted-foreground mt-1">Power users segment</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Average Conversion</p>
            <p className="text-lg font-bold text-primary">14.7 days</p>
            <p className="text-xs text-muted-foreground mt-1">All users average</p>
          </div>
        </div>
      </div>

      {/* Key Insights */}
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Icon name="Target" size={16} className="text-primary" />
          Key Conversion Patterns
        </h4>
        <div className="space-y-2">
          <div className="p-3 bg-card rounded-lg border border-border">
            <div className="flex items-start gap-2">
              <Icon name="TrendingUp" size={14} className="text-success mt-0.5" />
              <div>
                <p className="text-xs font-medium text-foreground">Early Feature Exploration</p>
                <p className="text-xs text-muted-foreground">Users who explore 3+ features within first week have 67% higher conversion rate</p>
              </div>
            </div>
          </div>
          <div className="p-3 bg-card rounded-lg border border-border">
            <div className="flex items-start gap-2">
              <Icon name="Clock" size={14} className="text-warning mt-0.5" />
              <div>
                <p className="text-xs font-medium text-foreground">Optimal Timing Window</p>
                <p className="text-xs text-muted-foreground">Days 7-14 show highest conversion likelihood - target with personalized offers</p>
              </div>
            </div>
          </div>
          <div className="p-3 bg-card rounded-lg border border-border">
            <div className="flex items-start gap-2">
              <Icon name="Users" size={14} className="text-primary mt-0.5" />
              <div>
                <p className="text-xs font-medium text-foreground">Social Proof Impact</p>
                <p className="text-xs text-muted-foreground">Users viewing testimonials convert 2.3x faster than those who don't</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumConversionTimeline;